// ── Replay Menu ──
// UI for listing, playing, deleting, and importing saved replays.
// Shown from the main menu "Replays" button.

import {
  ReplayStorage,
  type ReplayInfo,
  type ReplayImportError,
} from '../replay/replay-storage';
import { type ReplayData } from '../replay/replay-recorder';
import { isMobile } from '../core/device';

const REPLAY_RENAME_STORAGE_KEY = 'counter_pump_replay_renames';

export class ReplayMenu {
  private container: HTMLDivElement;
  private listEl: HTMLDivElement;
  private storage: ReplayStorage;
  private replayTitleOverrides: Record<string, string> = {};
  private visible = false;

  private onPlayReplay: ((replay: ReplayData) => void) | null = null;
  private onBack: (() => void) | null = null;
  // BUG-P-35: mirror the Delete debounce so a double-tap on Import can't
  // open the file picker twice (which leaks a second hidden <input>).
  private isImporting = false;

  constructor() {
    this.storage = new ReplayStorage();
    this.replayTitleOverrides = this.loadReplayTitleOverrides();

    this.container = document.createElement('div');
    this.container.id = 'replay-menu';
    this.container.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.9);
      display: none;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 500;
      font-family: 'Segoe UI', Arial, sans-serif;
      color: #fff;
    `;

    // Title
    const title = document.createElement('h2');
    title.textContent = 'Replays';
    title.style.cssText = `
      margin: 0 0 20px;
      font-size: 28px;
      font-weight: 600;
      color: #ffd700;
    `;

    // Replay list
    // BUG-P-18: width was a hard 600px which overflowed portrait phones.
    // Use a fluid width with viewport-relative cap so narrow screens fit.
    this.listEl = document.createElement('div');
    this.listEl.style.cssText = `
      width: min(600px, calc(100vw - 24px));
      max-height: 400px;
      overflow-y: auto;
      background: rgba(255,255,255,0.05);
      border: 1px solid rgba(255,255,255,0.15);
      border-radius: 8px;
      padding: 8px;
      box-sizing: border-box;
    `;

    // Button row
    const btnRow = document.createElement('div');
    btnRow.style.cssText = `
      display: flex;
      gap: 12px;
      margin-top: 16px;
    `;

    // Import button
    const importBtn = this.createButton('Import Replay');
    importBtn.addEventListener('click', () => this.importReplay());

    // Back button
    const backBtn = this.createButton('Back');
    backBtn.style.background = 'rgba(255,255,255,0.1)';
    backBtn.addEventListener('click', () => {
      this.hide();
      if (this.onBack) this.onBack();
    });

    btnRow.appendChild(importBtn);
    btnRow.appendChild(backBtn);

    this.container.appendChild(title);
    this.container.appendChild(this.listEl);
    this.container.appendChild(btnRow);
    document.body.appendChild(this.container);
  }

  setOnPlayReplay(callback: (replay: ReplayData) => void): void {
    this.onPlayReplay = callback;
  }

  setOnBack(callback: () => void): void {
    this.onBack = callback;
  }

  show(): void {
    this.container.style.display = 'flex';
    this.visible = true;
    this.refreshList();
  }

  hide(): void {
    this.container.style.display = 'none';
    this.visible = false;
  }

  isVisible(): boolean {
    return this.visible;
  }

  dispose(): void {
    this.container.remove();
  }

  // ── Internal ──

  private refreshList(): void {
    this.listEl.innerHTML = '';
    this.replayTitleOverrides = this.loadReplayTitleOverrides();

    const replays = this.storage.list();

    if (replays.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'No saved replays';
      empty.style.cssText = `
        text-align: center;
        color: rgba(255,255,255,0.5);
        padding: 40px;
        font-size: 14px;
      `;
      this.listEl.appendChild(empty);
      return;
    }

    // Sort by date descending (newest first)
    replays.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    for (const info of replays) {
      const row = this.createReplayRow(info);
      this.listEl.appendChild(row);
    }
  }

  private createReplayRow(info: ReplayInfo): HTMLDivElement {
    const mobile = isMobile();
    const row = document.createElement('div');
    // BUG-P-18: allow the row to wrap so Play/Download/Delete buttons stack
    // below the metadata on narrow screens instead of overflowing.
    row.style.cssText = `
      display: flex;
      flex-direction: ${mobile ? 'column' : 'row'};
      flex-wrap: wrap;
      align-items: ${mobile ? 'stretch' : 'center'};
      padding: 10px 12px;
      border-bottom: 1px solid rgba(255,255,255,0.08);
      gap: 12px;
    `;

    // Title / map name
    const titleWrap = document.createElement('div');
    titleWrap.style.cssText = `
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 120px;
    `;

    const mapEl = document.createElement('span');
    mapEl.textContent = this.getReplayTitle(info);
    mapEl.style.cssText = `
      font-weight: 600;
      color: #ffd700;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `;
    titleWrap.appendChild(mapEl);

    const renameBtn = this.createSmallButton('\u270E', '#ffd700');
    renameBtn.title = 'Rename';
    renameBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.startInlineRename(titleWrap, info);
    });
    titleWrap.appendChild(renameBtn);

    // Score
    const scoreEl = document.createElement('span');
    scoreEl.textContent = info.finalScore
      ? `${info.finalScore[1] ?? 0} - ${info.finalScore[2] ?? 0}`
      : '-';
    scoreEl.style.cssText = `
      color: rgba(255,255,255,0.7);
      min-width: 60px;
      text-align: center;
    `;

    // Duration
    const durEl = document.createElement('span');
    durEl.textContent = formatDuration(info.duration);
    durEl.style.cssText = `
      color: rgba(255,255,255,0.5);
      font-size: 12px;
      min-width: 60px;
    `;

    // Date
    const dateEl = document.createElement('span');
    dateEl.textContent = formatDate(info.date);
    dateEl.style.cssText = `
      color: rgba(255,255,255,0.4);
      font-size: 12px;
      min-width: 120px;
    `;

    // Spacer
    const spacer = document.createElement('div');
    spacer.style.flex = '1';

    // Play button
    const playBtn = this.createSmallButton('\u25B6 Play', '#4caf50');
    playBtn.addEventListener('click', () => {
      const replay = this.storage.load(info.key);
      if (replay && this.onPlayReplay) {
        this.hide();
        this.onPlayReplay(replay);
      }
    });

    // Download button
    const downloadBtn = this.createSmallButton('\u2193', '#2196f3');
    downloadBtn.title = 'Download';
    downloadBtn.addEventListener('click', () => {
      const replay = this.storage.load(info.key);
      if (replay) {
        const filename = `${info.map}_${info.date.replace(/[:.]/g, '-')}.replay`;
        ReplayStorage.download(replay, filename);
      }
    });

    // Delete button
    const deleteBtn = this.createSmallButton('\u2715', '#f44336');
    deleteBtn.title = 'Delete';
    deleteBtn.addEventListener('click', () => {
      // F-UI-011: Disable the button immediately so a double-tap can't fire
      // a second click against the next row that lands at the same DOM
      // index after refreshList() rebuilds the list.
      if (deleteBtn.disabled) return;
      deleteBtn.disabled = true;
      this.storage.delete(info.key);
      this.refreshList();
    });

    // Map preview thumbnail. Desktop sits at the left (60x40), mobile
    // stacks above the 2-line collapsed layout (80x52). Falls back to a
    // placeholder div with the map name when the image fails to load.
    const thumb = this.createMapThumbnail(info.map, mobile);

    if (mobile) {
      const line1 = document.createElement('div');
      Object.assign(line1.style, {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '12px',
        alignItems: 'center',
      });
      dateEl.style.minWidth = '0';
      scoreEl.style.minWidth = '0';
      line1.appendChild(dateEl);
      line1.appendChild(scoreEl);

      const line2 = document.createElement('div');
      Object.assign(line2.style, {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
      });
      titleWrap.style.minWidth = '0';
      titleWrap.style.flex = '1';
      durEl.style.minWidth = '0';
      line2.appendChild(titleWrap);
      line2.appendChild(durEl);
      line2.appendChild(playBtn);
      line2.appendChild(downloadBtn);
      line2.appendChild(deleteBtn);

      row.appendChild(thumb);
      row.appendChild(line1);
      row.appendChild(line2);
    } else {
      row.appendChild(thumb);
      row.appendChild(titleWrap);
      row.appendChild(scoreEl);
      row.appendChild(durEl);
      row.appendChild(dateEl);
      row.appendChild(spacer);
      row.appendChild(playBtn);
      row.appendChild(downloadBtn);
      row.appendChild(deleteBtn);
    }

    return row;
  }

  private createMapThumbnail(mapId: string, mobile: boolean): HTMLDivElement {
    const width = mobile ? 80 : 60;
    const height = mobile ? 52 : 40;

    const wrap = document.createElement('div');
    wrap.setAttribute('data-replay-thumb', mapId);
    wrap.style.cssText = `
      position: relative;
      width: ${width}px;
      height: ${height}px;
      flex-shrink: 0;
      border-radius: 3px;
      overflow: hidden;
      background: rgba(255,255,255,0.06);
      border: 1px solid rgba(255,255,255,0.1);
    `;
    if (mobile) {
      wrap.style.alignSelf = 'center';
    }

    const img = document.createElement('img');
    img.src = `/maps/${mapId}/preview.jpg`;
    img.alt = mapId;
    img.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    `;
    img.addEventListener('error', () => {
      img.remove();
      const fallback = document.createElement('div');
      fallback.setAttribute('data-replay-thumb-fallback', '1');
      fallback.textContent = mapId;
      fallback.style.cssText = `
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-size: 11px;
        color: rgba(255,255,255,0.7);
        padding: 2px;
        box-sizing: border-box;
        word-break: break-word;
        line-height: 1.1;
      `;
      wrap.appendChild(fallback);
    });
    wrap.appendChild(img);

    return wrap;
  }

  private getReplayTitle(info: ReplayInfo): string {
    return this.replayTitleOverrides[info.key] || info.map;
  }

  private startInlineRename(host: HTMLElement, info: ReplayInfo): void {
    const currentTitle = this.getReplayTitle(info);
    host.innerHTML = '';

    const input = document.createElement('input');
    input.type = 'text';
    input.value = currentTitle;
    input.style.cssText = `
      width: 100%;
      min-width: 140px;
      background: rgba(0,0,0,0.55);
      border: 1px solid rgba(255,215,0,0.55);
      color: #fff;
      padding: 4px 6px;
      border-radius: 3px;
      font-size: 13px;
      box-sizing: border-box;
    `;

    const save = () => {
      this.setReplayTitle(info.key, input.value.trim(), info.map);
      this.refreshList();
    };
    const cancel = () => this.refreshList();

    input.addEventListener('click', (e) => e.stopPropagation());
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        save();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    });

    host.appendChild(input);
    input.focus();
    input.select();
  }

  private loadReplayTitleOverrides(): Record<string, string> {
    try {
      const raw = localStorage.getItem(REPLAY_RENAME_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      const result: Record<string, string> = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (typeof value === 'string' && value.trim()) result[key] = value;
      }
      return result;
    } catch {
      return {};
    }
  }

  private setReplayTitle(key: string, title: string, fallbackTitle: string): void {
    if (title && title !== fallbackTitle) {
      this.replayTitleOverrides[key] = title;
    } else {
      delete this.replayTitleOverrides[key];
    }
    try {
      localStorage.setItem(REPLAY_RENAME_STORAGE_KEY, JSON.stringify(this.replayTitleOverrides));
    } catch {
      showReplayToast('Could not save replay title.', '#f44336');
    }
  }

  private async importReplay(): Promise<void> {
    // BUG-P-35: debounce repeated import taps. The flag is cleared once the
    // file picker resolves (change or cancel) so subsequent imports work.
    if (this.isImporting) return;
    this.isImporting = true;

    const input = document.createElement('input');
    input.type = 'file';
    // BUG-P-35: iOS Safari ignores the bare `.replay` extension when no MIME
    // type is present; advertising application/json first lets users pick a
    // .replay (which is JSON under the hood) from the Files app.
    input.accept = 'application/json,.replay';
    const release = () => {
      this.isImporting = false;
    };
    // Cancel path: focus returns to the window without a file. There is no
    // standard "cancel" event for <input type="file">; fall back to a
    // one-shot focus listener after the picker opens.
    const cancelListener = () => {
      // Wait a tick so a real `change` fires first if the user picked a file.
      setTimeout(() => {
        if (!input.files || input.files.length === 0) release();
      }, 300);
      window.removeEventListener('focus', cancelListener);
    };
    window.addEventListener('focus', cancelListener);

    input.addEventListener('change', async () => {
      try {
        const file = input.files?.[0];
        if (!file) return;

        const result = await ReplayStorage.importFromFile(file);
        if (!result.ok) {
          showReplayToast(replayImportErrorMessage(result.reason), '#f44336');
          return;
        }

        try {
          this.storage.save(result.replay);
        } catch {
          showReplayToast('Could not save replay: browser storage is full.', '#f44336');
          return;
        }

        this.refreshList();
        showReplayToast('Replay imported.', '#4caf50');
      } finally {
        release();
      }
    });
    input.click();
  }

  private createButton(text: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = text;
    btn.style.cssText = `
      background: rgba(255,215,0,0.2);
      border: 1px solid rgba(255,215,0,0.4);
      color: #fff;
      font-size: 14px;
      padding: 8px 20px;
      border-radius: 4px;
      cursor: pointer;
      font-weight: 500;
    `;
    return btn;
  }

  private createSmallButton(text: string, color: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = text;
    btn.style.cssText = `
      background: ${color}33;
      border: 1px solid ${color}88;
      color: #fff;
      font-size: 12px;
      padding: 4px 10px;
      border-radius: 3px;
      cursor: pointer;
    `;
    return btn;
  }
}

// ── Helpers ──

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}m ${secs}s`;
}

function formatDate(isoDate: string): string {
  try {
    const d = new Date(isoDate);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoDate;
  }
}

const REPLAY_TOAST_DURATION_MS = 3000;

function replayImportErrorMessage(reason: ReplayImportError): string {
  switch (reason) {
    case 'too_large':
      return 'Replay file is too large (50 MB max).';
    case 'parse_error':
      return 'Replay file is not valid JSON.';
    case 'unsupported_version':
      return 'Replay file is from an unsupported version.';
    case 'invalid_structure':
      return 'Replay file is missing required fields.';
  }
}

function showReplayToast(message: string, color: string): void {
  const toast = document.createElement('div');
  toast.setAttribute('data-replay-toast', '1');
  Object.assign(toast.style, {
    position: 'fixed',
    bottom: '80px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '8px 20px',
    background: 'rgba(0,0,0,0.85)',
    border: `1px solid ${color}`,
    borderRadius: '4px',
    color,
    fontFamily: '"Segoe UI", Arial, sans-serif',
    fontSize: '14px',
    fontWeight: '500',
    letterSpacing: '0.5px',
    zIndex: '600',
    transition: 'opacity 0.3s',
    pointerEvents: 'none',
  });
  toast.textContent = message;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 300);
  }, REPLAY_TOAST_DURATION_MS);
}
